import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import BrandedHero from "@/components/BrandedHero";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * AdminLandlordDetail — dedicated page for a single landlord.
 * Sections: Overview · Properties · Compliance (W-9 / Insurance) ·
 * Chain of Ownership · Contact.
 * Route: /admin/landlord/:id
 */
export default function AdminLandlordDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [compForm, setCompForm] = useState({});

  const load = () => {
    api.get(`/landlords/${id}/detail`)
      .then(r => {
        setData(r.data);
        setCompForm(r.data.compliance || {});
      })
      .catch(() => setData({ error: true }));
  };
  useEffect(() => { load(); }, [id]);

  const saveCompliance = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.patch(`/landlords/${id}`, compForm);
      toast.success("Compliance updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!data) {
    return (
      <AdminShell>
        <BrandedHero title="Loading landlord…" size="sm"/>
      </AdminShell>
    );
  }
  if (data.error) {
    return (
      <AdminShell>
        <BrandedHero title="Landlord not found" size="sm"/>
        <div className="p-6 text-center">
          <Link to="/admin/landlords" className="text-cyan-600 font-black text-sm">← All landlords</Link>
        </div>
      </AdminShell>
    );
  }

  const { landlord, properties, totals, siblings } = data;
  const chips = [];
  if (landlord.entity_type) chips.push({ label: landlord.entity_type });
  if (landlord.pm_name) chips.push({ label: `PM · ${landlord.pm_name}` });
  chips.push({ icon: "🏠", label: `${totals.properties} propert${totals.properties === 1 ? "y" : "ies"}` });
  if (totals.open_wos > 0) chips.push({ icon: "🧾", label: `${totals.open_wos} open WOs` });
  if (totals.ytd_spend > 0) chips.push({ icon: "💰", label: `$${totals.ytd_spend.toLocaleString()} YTD` });

  return (
    <AdminShell>
      <BrandedHero
        breadcrumbs={[
          { to: "/admin?v=v2", label: "← Dashboard" },
          { to: "/admin/landlords", label: "Landlords" },
          { label: landlord.name },
        ]}
        eyebrow="Landlord"
        title={landlord.name}
        subtitle={
          <>
            {landlord.email && <a href={`mailto:${landlord.email}`} className="text-white underline decoration-white/40 hover:decoration-white">{landlord.email}</a>}
            {landlord.phone && <> · <a href={`tel:${landlord.phone}`} className="text-white underline decoration-white/40 hover:decoration-white">{landlord.phone}</a></>}
          </>
        }
        chips={chips}
        size="md"
        actions={
          <button
            data-testid="landlord-edit-btn"
            onClick={() => nav(`/admin/landlords?focus=${id}`)}
            className="h-9 px-3 rounded-full bg-white text-slate-900 text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 inline-flex items-center">
            Edit
          </button>
        }
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT 2/3 — Properties + Compliance */}
          <div className="lg:col-span-2 space-y-4">
            {/* KPI strip */}
            <div className="grid grid-cols-4 gap-3" data-testid="landlord-kpis">
              <KpiTile label="Properties" value={totals.properties} color="#00C4CC"/>
              <KpiTile label="Open WOs"   value={totals.open_wos}   color="#FF6A2B"/>
              <KpiTile label="Total WOs"  value={totals.total_wos}  color="#8B93A7"/>
              <KpiTile label="YTD Spend"  value={`$${(totals.ytd_spend || 0).toLocaleString()}`} color="#22C6A6"/>
            </div>

            {/* Properties table */}
            <Card testid="landlord-properties">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-black text-[#0F1F3A]">Properties</h3>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{properties.length} linked</span>
              </div>
              {properties.length === 0 ? (
                <EmptyState label="No properties linked yet"
                  hint="Head to Properties → assign this landlord to link doors."/>
              ) : (
                <div className="divide-y divide-slate-100">
                  {properties.map(p => (
                    <Link key={p.id} to={`/admin/properties/${encodeURIComponent(p.address)}`}
                      data-testid={`landlord-property-${p.id}`}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-[#0F1F3A] truncate">
                          {p.address}
                          {p.unit && <span className="text-[#8B93A7] font-medium"> · {p.unit}</span>}
                        </div>
                        <div className="text-[10px] text-[#8B93A7] font-medium">
                          {p.last_visit ? `Last visit ${new Date(p.last_visit).toLocaleDateString()}` : "No visits yet"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {p.open_wos > 0 && (
                          <span className="text-[9px] font-black uppercase tracking-widest text-orange-800 bg-orange-100 border border-orange-200 rounded-full px-2 py-0.5">
                            {p.open_wos} open
                          </span>
                        )}
                        <span className="text-[10px] font-black tabular-nums text-[#8B93A7]">
                          ${(p.spend_ytd || 0).toLocaleString()}
                        </span>
                        <span className="text-[#8B93A7] text-sm">→</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            {/* Compliance */}
            <Card testid="landlord-compliance">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-black text-[#0F1F3A]">Compliance</h3>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">W-9 · Insurance · Entity</span>
                <button
                  onClick={saveCompliance}
                  disabled={saving}
                  data-testid="landlord-compliance-save"
                  className="ml-auto h-8 px-3 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-[10px] font-black uppercase tracking-widest">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SelectField label="W-9 Status" value={compForm.w9_status || "missing"}
                  testid="landlord-w9-status"
                  onChange={v => setCompForm({...compForm, w9_status: v})}
                  options={[
                    { v: "missing", l: "Missing" },
                    { v: "on_file", l: "On File" },
                    { v: "expired", l: "Expired" },
                  ]}/>
                <TextField label="W-9 Updated" type="date"
                  testid="landlord-w9-updated"
                  value={(compForm.w9_updated_at || "").slice(0, 10)}
                  onChange={v => setCompForm({...compForm, w9_updated_at: v ? v + "T12:00:00Z" : null})}/>

                <SelectField label="Insurance Status" value={compForm.insurance_status || "missing"}
                  testid="landlord-ins-status"
                  onChange={v => setCompForm({...compForm, insurance_status: v})}
                  options={[
                    { v: "missing", l: "Missing" },
                    { v: "on_file", l: "On File" },
                    { v: "expired", l: "Expired" },
                  ]}/>
                <TextField label="Insurance Expires" type="date"
                  testid="landlord-ins-expires"
                  value={(compForm.insurance_expires || "").slice(0, 10)}
                  onChange={v => setCompForm({...compForm, insurance_expires: v ? v + "T12:00:00Z" : null})}/>

                <SelectField label="Entity Type" value={compForm.entity_type || ""}
                  testid="landlord-entity-type"
                  onChange={v => setCompForm({...compForm, entity_type: v})}
                  options={[
                    { v: "", l: "— None —" },
                    { v: "Individual", l: "Individual" },
                    { v: "LLC", l: "LLC" },
                    { v: "S-Corp", l: "S-Corp" },
                    { v: "C-Corp", l: "C-Corp" },
                    { v: "Trust", l: "Trust" },
                    { v: "Partnership", l: "Partnership" },
                  ]}/>
                <TextField label="EIN — last 4" placeholder="e.g. 4477"
                  testid="landlord-ein-last4" maxLength={6}
                  value={compForm.ein_last4 || ""}
                  onChange={v => setCompForm({...compForm, ein_last4: v})}/>
              </div>
              <div className="mt-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">Compliance notes</label>
                <textarea
                  data-testid="landlord-compliance-notes"
                  value={compForm.notes_compliance || ""}
                  onChange={e => setCompForm({...compForm, notes_compliance: e.target.value})}
                  className="mt-1 w-full h-20 p-2 border border-slate-200 rounded-lg text-sm resize-y focus:border-cyan-400 focus:outline-none"
                  placeholder="Anything the bookkeeper should know…"
                />
              </div>
            </Card>
          </div>

          {/* RIGHT 1/3 — Contact + Chain of Ownership */}
          <div className="space-y-4">
            {/* Contact */}
            <Card testid="landlord-contact">
              <h3 className="text-lg font-black text-[#0F1F3A] mb-3">Contact</h3>
              <div className="space-y-1.5 text-sm">
                <ContactRow icon="📧" value={landlord.email}
                  href={landlord.email ? `mailto:${landlord.email}` : null}/>
                <ContactRow icon="📞" value={landlord.phone}
                  href={landlord.phone ? `tel:${landlord.phone}` : null}/>
                {landlord.pm_name && <ContactRow icon="🏢" value={`PM · ${landlord.pm_name}`}/>}
                {landlord.coordinator_name && <ContactRow icon="🧑‍💼" value={`Coord · ${landlord.coordinator_name}`}/>}
              </div>
              {landlord.notes && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] mb-1">Notes</div>
                  <p className="text-sm text-[#0F1F3A] whitespace-pre-wrap">{landlord.notes}</p>
                </div>
              )}
            </Card>

            {/* Chain of Ownership */}
            <Card testid="landlord-siblings">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-black text-[#0F1F3A]">Chain of Ownership</h3>
                <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
                  {landlord.principal_name || "no principal"}
                </span>
              </div>
              {!landlord.principal_name ? (
                <EmptyState label="No principal set"
                  hint='Add a Principal Name in the edit modal to group multiple LLCs under one owner.'/>
              ) : siblings.length === 0 ? (
                <EmptyState label="Only entity under this principal"
                  hint={`No other landlords tagged as “${landlord.principal_name}”.`}/>
              ) : (
                <div className="space-y-1">
                  {siblings.map(s => (
                    <Link key={s.id} to={`/admin/landlord/${s.id}`}
                      data-testid={`landlord-sibling-${s.id}`}
                      className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                      <span className="h-1.5 w-1.5 rounded-full bg-violet-500 flex-shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black text-[#0F1F3A] truncate">{s.name}</div>
                        <div className="text-[10px] text-[#8B93A7] font-medium truncate">{s.email || "no email"}</div>
                      </div>
                      <span className="text-[9px] font-black text-slate-500 tabular-nums flex-shrink-0">
                        {s.property_count} 🏠
                      </span>
                      <span className="text-[#8B93A7] text-sm flex-shrink-0">→</span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

/* ---- small helpers ---- */
function Card({ children, testid }) {
  return (
    <div data-testid={testid}
      className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4 sm:p-5">
      {children}
    </div>
  );
}
function KpiTile({ label, value, color }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-3">
      <div className="text-2xl font-black tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
function TextField({ label, value, onChange, placeholder = "", type = "text", maxLength, testid }) {
  return (
    <div>
      <label className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{label}</label>
      <input
        type={type}
        data-testid={testid}
        value={value || ""}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="mt-1 w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-cyan-400 focus:outline-none"
      />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{label}</label>
      <select data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)}
        className="mt-1 w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:border-cyan-400 focus:outline-none">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
function ContactRow({ icon, value, href }) {
  if (!value) return null;
  const body = (
    <>
      <span className="text-base">{icon}</span>
      <span className="text-sm text-[#0F1F3A] truncate">{value}</span>
    </>
  );
  return href ? (
    <a href={href} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded">{body}</a>
  ) : (
    <div className="flex items-center gap-2 p-1">{body}</div>
  );
}
function EmptyState({ label, hint }) {
  return (
    <div className="text-center py-6">
      <div className="text-sm font-black text-[#0F1F3A]">{label}</div>
      {hint && <div className="text-[11px] text-[#8B93A7] mt-1">{hint}</div>}
    </div>
  );
}
